import Footer from "@/components/layout/Footer";
import Header from "@/components/layout/Header";
import {
  allArticles,
  getArticleAuthors,
  getArticleCategories,
  getAuthorImage,
} from "@/data/articles";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { scrollToTop } from "@/utils";
import {
  BookOpen,
  Calendar,
  Clock,
  Filter,
  Newspaper,
  Search,
  Sparkles,
  Tag,
  User,
  Users,
  X,
} from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";

const PageArticles = () => {
  const [selectedCategory, setSelectedCategory] = useState<string>("all");
  const [selectedAuthor, setSelectedAuthor] = useState<string>("all");
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [showSuggestions, setShowSuggestions] = useState<boolean>(false);
  const searchContainerRef = useRef<HTMLDivElement>(null);

  const categories = useMemo(() => ["all", ...getArticleCategories()], []);
  const authors = useMemo(() => getArticleAuthors(), []);

  // Close search suggestions dropdown on click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        searchContainerRef.current &&
        !searchContainerRef.current.contains(event.target as Node)
      ) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const filteredArticles = useMemo(() => {
    return allArticles.filter((article) => {
      const matchesCategory =
        selectedCategory === "all" || article.category === selectedCategory;

      let matchesAuthor = true;
      if (selectedAuthor !== "all") {
        const authLower = article.author.toLowerCase();
        if (selectedAuthor === "serge-joseph") {
          matchesAuthor =
            authLower.includes("serge") &&
            !authLower.includes("jr") &&
            !authLower.includes("junior");
        } else if (selectedAuthor === "serge-joseph-jr") {
          matchesAuthor =
            authLower.includes("jr") || authLower.includes("junior");
        } else if (selectedAuthor === "othniel-joseph") {
          matchesAuthor = authLower.includes("othniel");
        } else if (selectedAuthor === "jimmy-jeanty") {
          matchesAuthor =
            authLower.includes("jimmy") || authLower.includes("jeanty");
        } else {
          matchesAuthor = authLower.includes(selectedAuthor.toLowerCase());
        }
      }

      const q = searchQuery.toLowerCase().trim();
      const matchesSearch =
        !q ||
        article.title.toLowerCase().includes(q) ||
        article.subtitle.toLowerCase().includes(q) ||
        article.summary.toLowerCase().includes(q) ||
        article.author.toLowerCase().includes(q) ||
        article.tags.some((tag) => tag.toLowerCase().includes(q));

      return matchesCategory && matchesAuthor && matchesSearch;
    });
  }, [selectedCategory, selectedAuthor, searchQuery]);

  // Autocomplete suggestions dropdown when typing search query
  const searchSuggestions = useMemo(() => {
    const q = searchQuery.toLowerCase().trim();
    if (!q || q.length < 2) return null;

    const matchedAuthors = authors.filter(
      (a) =>
        a.name.toLowerCase().includes(q) || a.role.toLowerCase().includes(q),
    );

    const matchedArticles = allArticles
      .filter(
        (art) =>
          art.title.toLowerCase().includes(q) ||
          art.tags.some((t) => t.toLowerCase().includes(q)),
      )
      .slice(0, 3);

    if (matchedAuthors.length === 0 && matchedArticles.length === 0) return null;

    return {
      authors: matchedAuthors,
      articles: matchedArticles,
    };
  }, [searchQuery, authors]);

  const featuredArticle = allArticles[0];

  return (
    <div className="min-h-screen bg-slate-50 font-sans flex flex-col justify-between">
      <Helmet>
        <title>
          Articles & Réflexions Spirituelles | Église de Dieu Garizim
        </title>
        <meta
          name="description"
          content="Découvrez nos articles et réflexions théologiques rédigés par l'équipe pastorale de Garizim : enseignements bibliques, foi chrétienne, prière, vie de famille et histoire de l'église."
        />
        <link rel="canonical" href="https://egarizim.com/articles" />
      </Helmet>

      <Header />

      <main className="pt-24 pb-16">
        {/* Hero Section */}
        <section className="bg-gradient-to-b from-slate-950 via-slate-900 to-slate-800 text-white py-14 sm:py-20 px-4 relative overflow-hidden">
          <div className="container mx-auto max-w-5xl text-center space-y-4">
            <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-white/10 border border-white/15 text-amber-300 text-xs font-bold uppercase tracking-wider backdrop-blur-sm">
              <Newspaper className="w-4 h-4" />
              <span>Publications Pastorales & Doctrinales</span>
            </div>
            <h1 className="text-3xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-tight">
              Articles, Études & Réflexions
            </h1>
            <p className="text-slate-300 text-sm sm:text-lg max-w-2xl mx-auto leading-relaxed">
              Nourrissez votre foi avec nos prédications écrites, analyses
              théologiques et guides bibliques rédigés par les pasteurs et
              enseignants de l'Église de Dieu Garizim.
            </p>
          </div>
        </section>

        {/* Content Area */}
        <section className="container mx-auto max-w-7xl px-4 sm:px-6 -mt-8 relative z-20 space-y-10">
          {/* Featured Article Banner (Only shown if no active filter) */}
          {selectedCategory === "all" &&
            selectedAuthor === "all" &&
            !searchQuery &&
            featuredArticle && (
            <div className="bg-slate-700/80 text-white rounded-3xl shadow-xl border border-slate-600/70 overflow-hidden hover:shadow-2xl hover:border-slate-500/80 hover:bg-slate-700/95 transition-all duration-300">
              <div className="p-6 sm:p-10 lg:p-12 space-y-6">
                <div className="flex flex-wrap items-center gap-2.5">
                  <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold bg-amber-500/20 text-amber-300 border border-amber-500/30">
                    <Sparkles className="w-3.5 h-3.5 text-amber-400" />À la Une
                  </span>
                  <span className="px-3 py-1 rounded-full text-xs font-semibold bg-slate-800/60 text-slate-100 border border-slate-600/70">
                    {featuredArticle.category}
                  </span>
                  <span className="flex items-center gap-1 text-xs text-slate-300">
                    <Calendar className="w-3.5 h-3.5" />
                    {featuredArticle.date}
                  </span>
                  <span className="flex items-center gap-1 text-xs text-slate-300">
                    <Clock className="w-3.5 h-3.5" />
                    {featuredArticle.readTime}
                  </span>
                </div>

                <div className="space-y-3">
                  <h2 className="text-2xl sm:text-3xl lg:text-4xl font-black text-white tracking-tight hover:text-amber-200 transition-colors">
                    <Link
                      to={`/articles/${featuredArticle.slug}`}
                      onClick={() => scrollToTop()}
                    >
                      {featuredArticle.title}
                    </Link>
                  </h2>
                  <p className="text-slate-200 text-sm sm:text-base leading-relaxed line-clamp-3">
                    {featuredArticle.summary}
                  </p>
                </div>

                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pt-4 border-t border-slate-600/70">
                  <div className="flex items-center gap-3">
                    {(() => {
                      const featImg = getAuthorImage(
                        featuredArticle.author,
                        featuredArticle.authorImage,
                      );
                      return featImg ? (
                        <img
                          src={featImg}
                          alt={featuredArticle.author}
                          className="w-10 h-10 rounded-full object-cover border-2 border-amber-400/80 shadow shrink-0"
                        />
                      ) : (
                        <div className="w-10 h-10 rounded-full bg-slate-800 text-white flex items-center justify-center font-bold text-xs shrink-0">
                          <User className="w-4 h-4 text-amber-400" />
                        </div>
                      );
                    })()}
                    <div>
                      <div className="text-xs sm:text-sm font-bold text-white">
                        {featuredArticle.author}
                      </div>
                      <div className="text-[11px] text-slate-300">
                        {featuredArticle.authorRole}
                      </div>
                    </div>
                  </div>

                  <Link
                    to={`/articles/${featuredArticle.slug}`}
                    onClick={() => scrollToTop()}
                    className="inline-flex items-center justify-center gap-2 px-6 py-2.5 rounded-full bg-amber-400 hover:bg-amber-300 text-slate-900 text-xs sm:text-sm font-bold shadow-md hover:scale-105 transition-all"
                  >
                    <span>Lire la réflexion complète</span>
                    <span>&rarr;</span>
                  </Link>
                </div>
              </div>
            </div>
          )}

          {/* Search and Category Filtering Toolbar */}
          <div className="bg-white rounded-2xl p-4 sm:p-6 shadow-md border border-slate-200/80 space-y-4">
            <div className="flex flex-col lg:flex-row gap-3 items-stretch lg:items-center justify-between">
              <div className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-center flex-1">
                {/* Search Bar with Autocomplete Suggestions */}
                <div ref={searchContainerRef} className="relative flex-1">
                  <Search className="w-4 h-4 text-slate-400 absolute left-3.5 top-1/2 -translate-y-1/2 pointer-events-none" />
                  <input
                    type="text"
                    placeholder="Rechercher par mot-clé, thème ou auteur..."
                    value={searchQuery}
                    onChange={(e) => {
                      setSearchQuery(e.target.value);
                      setShowSuggestions(true);
                    }}
                    onFocus={() => setShowSuggestions(true)}
                    className="w-full pl-10 pr-10 py-2.5 text-xs sm:text-sm rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-church-blue-500 bg-slate-50 transition-all"
                  />
                  {searchQuery && (
                    <button
                      type="button"
                      onClick={() => setSearchQuery("")}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-slate-400 hover:text-slate-600 p-1"
                      aria-label="Effacer la recherche"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  )}

                  {/* Suggestions Popover */}
                  {showSuggestions && searchSuggestions && (
                    <div className="absolute left-0 right-0 top-full mt-1.5 bg-white rounded-xl shadow-2xl border border-slate-200 z-50 overflow-hidden divide-y divide-slate-100 animate-in fade-in-50 duration-150">
                      {searchSuggestions.authors.length > 0 && (
                        <div className="p-2">
                          <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400 px-2.5 py-1">
                            Auteurs / Prédicateurs
                          </div>
                          {searchSuggestions.authors.map((auth) => (
                            <button
                              key={auth.id}
                              type="button"
                              onClick={() => {
                                setSelectedAuthor(auth.id);
                                setSearchQuery("");
                                setShowSuggestions(false);
                              }}
                              className="w-full flex items-center gap-2.5 px-2.5 py-2 rounded-lg hover:bg-slate-50 transition-colors text-left group"
                            >
                              {auth.image ? (
                                <img
                                  src={auth.image}
                                  alt={auth.name}
                                  className="w-6 h-6 rounded-full object-cover shrink-0 border border-slate-200"
                                />
                              ) : (
                                <User className="w-4 h-4 text-slate-400 shrink-0" />
                              )}
                              <div className="flex-1 truncate">
                                <div className="text-xs font-bold text-slate-800 group-hover:text-church-blue-700">
                                  {auth.name}
                                </div>
                                <div className="text-[10px] text-slate-500 truncate">
                                  {auth.role}
                                </div>
                              </div>
                              <span className="text-[10px] font-semibold text-church-blue-600 bg-church-blue-50 px-2 py-0.5 rounded-full shrink-0">
                                Filtrer
                              </span>
                            </button>
                          ))}
                        </div>
                      )}

                      {searchSuggestions.articles.length > 0 && (
                        <div className="p-2">
                          <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400 px-2.5 py-1">
                            Articles
                          </div>
                          {searchSuggestions.articles.map((art) => (
                            <Link
                              key={art.id}
                              to={`/articles/${art.slug}`}
                              onClick={() => {
                                setShowSuggestions(false);
                                scrollToTop();
                              }}
                              className="w-full flex items-center gap-2.5 px-2.5 py-2 rounded-lg hover:bg-slate-50 transition-colors text-left group"
                            >
                              <BookOpen className="w-4 h-4 text-slate-400 shrink-0 group-hover:text-church-blue-600" />
                              <div className="flex-1 truncate">
                                <div className="text-xs font-semibold text-slate-800 truncate group-hover:text-church-blue-700">
                                  {art.title}
                                </div>
                                <div className="text-[10px] text-slate-400 flex items-center gap-2">
                                  <span>{art.category}</span>
                                  <span>•</span>
                                  <span>{art.author}</span>
                                </div>
                              </div>
                            </Link>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Author Dropdown Selector */}
                <div className="w-full sm:w-[260px] shrink-0">
                  <Select
                    value={selectedAuthor}
                    onValueChange={(val) => setSelectedAuthor(val)}
                  >
                    <SelectTrigger className="w-full h-[42px] rounded-xl bg-slate-50 border-slate-200 text-xs sm:text-sm font-medium focus:ring-church-blue-500">
                      <div className="flex items-center gap-2 truncate">
                        <Users className="w-4 h-4 text-church-blue-600 shrink-0" />
                        <SelectValue placeholder="Tous les auteurs" />
                      </div>
                    </SelectTrigger>
                    <SelectContent className="bg-white rounded-xl shadow-2xl border border-slate-200 z-50">
                      <SelectItem
                        value="all"
                        className="text-xs sm:text-sm font-semibold"
                      >
                        <div className="flex items-center gap-2 py-0.5">
                          <Users className="w-4 h-4 text-slate-400" />
                          <span>Tous les auteurs / prédicateurs</span>
                        </div>
                      </SelectItem>
                      {authors.map((auth) => (
                        <SelectItem
                          key={auth.id}
                          value={auth.id}
                          className="text-xs sm:text-sm"
                        >
                          <div className="flex items-center gap-2.5 py-1">
                            {auth.image ? (
                              <img
                                src={auth.image}
                                alt={auth.name}
                                className="w-6 h-6 rounded-full object-cover shrink-0 border border-slate-200"
                              />
                            ) : (
                              <User className="w-4 h-4 text-slate-400 shrink-0" />
                            )}
                            <div className="flex flex-col text-left">
                              <span className="font-semibold text-slate-900 leading-snug">
                                {auth.name}
                              </span>
                              <span className="text-[10px] text-slate-500 leading-tight">
                                {auth.role}
                              </span>
                            </div>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Stats badge */}
              <div className="text-xs text-slate-500 font-medium self-end lg:self-center shrink-0">
                {filteredArticles.length} article
                {filteredArticles.length > 1 ? "s" : ""} disponible
                {filteredArticles.length > 1 ? "s" : ""}
              </div>
            </div>

            {/* Active Filters Bar */}
            {(selectedCategory !== "all" ||
              selectedAuthor !== "all" ||
              searchQuery) && (
              <div className="flex flex-wrap items-center gap-2 pt-2 border-t border-slate-100 text-xs">
                <span className="text-slate-400 font-semibold text-[11px]">
                  Filtres actifs :
                </span>

                {selectedAuthor !== "all" && (
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-amber-50 border border-amber-200 text-amber-900 font-medium">
                    <User className="w-3 h-3 text-amber-600" />
                    <span>
                      {authors.find((a) => a.id === selectedAuthor)?.name ||
                        selectedAuthor}
                    </span>
                    <button
                      type="button"
                      onClick={() => setSelectedAuthor("all")}
                      className="ml-1 hover:text-amber-700 font-bold p-0.5 rounded-full"
                      title="Retirer le filtre prédicateur"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                )}

                {selectedCategory !== "all" && (
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-church-blue-50 border border-church-blue-200 text-church-blue-800 font-medium">
                    <Tag className="w-3 h-3 text-church-blue-600" />
                    <span>{selectedCategory}</span>
                    <button
                      type="button"
                      onClick={() => setSelectedCategory("all")}
                      className="ml-1 hover:text-church-blue-900 font-bold p-0.5 rounded-full"
                      title="Retirer le filtre catégorie"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                )}

                {searchQuery && (
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-slate-100 border border-slate-200 text-slate-700 font-medium">
                    <Search className="w-3 h-3 text-slate-500" />
                    <span>"{searchQuery}"</span>
                    <button
                      type="button"
                      onClick={() => setSearchQuery("")}
                      className="ml-1 hover:text-slate-900 font-bold p-0.5 rounded-full"
                      title="Effacer le mot-clé"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                )}

                <button
                  type="button"
                  onClick={() => {
                    setSelectedCategory("all");
                    setSelectedAuthor("all");
                    setSearchQuery("");
                  }}
                  className="text-[11px] text-slate-400 hover:text-red-600 underline ml-auto transition-colors font-medium"
                >
                  Tout réinitialiser
                </button>
              </div>
            )}

            {/* Category Filter Pills */}
            <div className="flex items-center gap-2 overflow-x-auto pb-1 scrollbar-none">
              <span className="text-xs font-bold text-slate-400 flex items-center gap-1 shrink-0 mr-1">
                <Filter className="w-3.5 h-3.5" />
                Thèmes :
              </span>
              {categories.map((cat) => {
                const isSelected = selectedCategory === cat;
                const label = cat === "all" ? "Tous les articles" : cat;

                return (
                  <button
                    key={cat}
                    onClick={() => setSelectedCategory(cat)}
                    className={`px-3.5 py-1.5 rounded-full text-xs font-bold shrink-0 transition-all ${
                      isSelected
                        ? "bg-church-blue-800 text-white shadow-sm"
                        : "bg-slate-100 hover:bg-slate-200 text-slate-700"
                    }`}
                  >
                    {label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Articles Grid */}
          {filteredArticles.length === 0 ? (
            <div className="bg-slate-700/80 text-white rounded-2xl p-12 text-center space-y-3 border border-slate-600/70">
              <BookOpen className="w-10 h-10 text-slate-300 mx-auto" />
              <h3 className="text-lg font-bold text-white">
                Aucun article ne correspond à votre recherche
              </h3>
              <p className="text-xs sm:text-sm text-slate-300 max-w-md mx-auto">
                Essayez d'autres mots-clés ou réinitialisez la sélection des
                catégories ou de l'auteur pour afficher toutes nos publications.
              </p>
              <button
                onClick={() => {
                  setSelectedCategory("all");
                  setSelectedAuthor("all");
                  setSearchQuery("");
                }}
                className="mt-2 text-xs font-bold text-amber-300 hover:text-amber-200 underline"
              >
                Réinitialiser les filtres
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredArticles.map((article) => (
                <article
                  key={article.id}
                  className="bg-slate-700/80 text-white rounded-2xl border border-slate-600/70 shadow-sm hover:shadow-xl hover:border-slate-500/80 hover:bg-slate-700/95 transition-all duration-300 flex flex-col justify-between overflow-hidden group"
                >
                  <div className="p-5 sm:p-6 space-y-3.5">
                    {/* Meta row */}
                    <div className="flex items-center justify-between gap-2 text-xs">
                      <span className="px-2.5 py-1 rounded-full bg-slate-800/60 text-slate-100 border border-slate-600/70 font-semibold group-hover:bg-slate-800/90 transition-colors">
                        {article.category}
                      </span>
                      <span className="text-slate-300 flex items-center gap-1 text-[11px]">
                        <Clock className="w-3 h-3 text-slate-300" />
                        {article.readTime}
                      </span>
                    </div>

                    {/* Title & Subtitle */}
                    <div className="space-y-1.5">
                      <h3 className="text-lg font-bold text-white group-hover:text-amber-200 transition-colors line-clamp-2 leading-snug">
                        <Link
                          to={`/articles/${article.slug}`}
                          onClick={() => scrollToTop()}
                        >
                          {article.title}
                        </Link>
                      </h3>
                      <p className="text-xs text-slate-300 line-clamp-2 leading-relaxed">
                        {article.subtitle}
                      </p>
                    </div>

                    {/* Summary text */}
                    <p className="text-xs sm:text-sm text-slate-200/90 line-clamp-3 leading-relaxed">
                      {article.summary}
                    </p>

                    {/* Tags */}
                    <div className="flex flex-wrap gap-1.5 pt-1">
                      {article.tags.slice(0, 3).map((tag) => (
                        <span
                          key={tag}
                          className="inline-flex items-center gap-1 text-[10px] text-slate-200 bg-slate-800/40 border border-slate-600/50 px-2 py-0.5 rounded-md"
                        >
                          <Tag className="w-2.5 h-2.5 text-slate-300" />
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>

                  {/* Card Footer */}
                  <div className="px-5 sm:px-6 py-3.5 bg-slate-800/50 border-t border-slate-600/60 flex items-center justify-between text-xs">
                    <button
                      type="button"
                      onClick={() => {
                        const matched = authors.find((a) =>
                          article.author
                            .toLowerCase()
                            .includes(
                              a.name.toLowerCase().replace("pasteur ", "").trim(),
                            ),
                        );
                        if (matched) {
                          setSelectedAuthor(matched.id);
                        } else {
                          setSelectedAuthor(article.author);
                        }
                      }}
                      className="text-[11px] text-slate-300 flex items-center gap-2 hover:text-white transition-colors group/author text-left"
                      title={`Filtrer par ${article.author}`}
                    >
                      {(() => {
                        const cardAuthorImg = getAuthorImage(
                          article.author,
                          article.authorImage,
                        );
                        return cardAuthorImg ? (
                          <img
                            src={cardAuthorImg}
                            alt={article.author}
                            className="w-6 h-6 rounded-full object-cover border border-slate-500/80 shrink-0"
                          />
                        ) : (
                          <User className="w-3.5 h-3.5 text-slate-300 shrink-0" />
                        );
                      })()}
                      <span className="font-medium text-slate-200 group-hover/author:text-white truncate max-w-[130px]">
                        {article.author}
                      </span>
                    </button>
                    <Link
                      to={`/articles/${article.slug}`}
                      onClick={() => scrollToTop()}
                      className="text-xs font-bold text-amber-300 hover:text-amber-200 flex items-center gap-1 transition-colors group-hover:translate-x-0.5"
                    >
                      <span>Lire</span>
                      <span>&rarr;</span>
                    </Link>
                  </div>
                </article>
              ))}
            </div>
          )}
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default PageArticles;
